import { useMemo, useCallback, useEffect } from 'react';

import { useOlderHistoryFetcher } from '../../../store/apiHooks/useHistoryFetcher';
import { useCoreDispatch } from '../../../store/CoreStore';
import { useHistorySlice } from '../../../store/slices/history/selectors';
import { historyActions } from '../../../store/slices/history/slice';

interface UseHistoryNavigationReturnType {
  handleNext: () => void;
  handlePrev: () => void;
  handleDetailsLoaded: () => void;
  isFirstGame: boolean;
  isLastGame: boolean;
  isLoadingDetails: boolean;
}

export const useHistoryNavigation = (): UseHistoryNavigationReturnType => {
  const dispatch = useCoreDispatch();
  const history = useHistorySlice.getHistory();
  const isLoadingDetails = useHistorySlice.getLoadingDetails();
  const gameInfo = useHistorySlice.getGameInfo();
  const olderHistoryFetcher = useOlderHistoryFetcher();

  const handleDetailsLoaded = useCallback(() => void dispatch(historyActions.setLoadingDetails(false)), [dispatch]);

  const currentGameIndex = useMemo(
    () => history.ids.findIndex((id) => id === gameInfo?.gameId),
    [history.ids, gameInfo?.gameId]
  );

  const isFirstGame = useMemo(() => currentGameIndex === 0, [currentGameIndex]);

  const isLastGame = useMemo(
    () => currentGameIndex === history.ids.length - 1 && !history.loadMore,
    [currentGameIndex, history.ids, history.loadMore]
  );

  const handleNext = useCallback(() => {
    if (isLoadingDetails) return;
    const nextGame = history.entities[history.ids[currentGameIndex + 1]];
    if (nextGame) {
      dispatch(historyActions.setLoadingDetails(true));
      dispatch(historyActions.setGameInfo(nextGame));
    }
  }, [isLoadingDetails, currentGameIndex, history.entities, history.ids, dispatch]);

  const handlePrev = useCallback((): void => {
    if (isLoadingDetails) return;
    const prevGame = history.entities[history.ids[currentGameIndex - 1]];
    if (prevGame) {
      dispatch(historyActions.setLoadingDetails(true));
      dispatch(historyActions.setGameInfo(prevGame));
    }
  }, [isLoadingDetails, history.entities, history.ids, currentGameIndex, dispatch]);

  useEffect(() => {
    if (!history || !history.ids.length) return;
    const lastHistoryIndex = history.ids.length - 1;
    if (history.ids[lastHistoryIndex] === gameInfo?.gameId && history.loadMore) {
      olderHistoryFetcher(history.page + 1);
    }
  }, [history, gameInfo, olderHistoryFetcher]);

  return {
    handleNext,
    handlePrev,
    handleDetailsLoaded,
    isFirstGame,
    isLastGame,
    isLoadingDetails,
  };
};
